import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { take } from 'rxjs';
import { CopyButtonComponent } from '../../../basic/buttons/copy-button/copy-button.component';
import { EditableTextComponent } from '../../../basic/editable-text/editable-text.component';
import { TruncatePipe } from '../../../basic/pipes/truncate.pipe';
import { PopoverEditTextComponent } from '../../../basic/popover/popover-edit-text/popover-edit-text.component';
import { BoardHolderService } from '../../../core/board/board.holder.service';
import { Leads, LeadsStatus } from '../../../core/leads/leads';
import { LeadsService } from '../../../core/leads/leads.service';
import { OpenaiService } from '../../../core/openai/openai.service';
import { ProductService } from '../../../core/product/product.service';
import { ResumeSource } from '../../../core/resume/resume';
import { ResumeService } from '../../../core/resume/resume.service';
import { UnipileService } from '../../../core/unipile/unipile.service';
import { UpgradeToPremiumComponent } from '../../../domain/membership/upgrade-to-premium/upgrade-to-premium.component';
import { NewEditMethodologyComponent } from '../../../domain/methodology/new-edit-methodology/new-edit-methodology.component';
import { ProfileCardSmallComponent } from '../../../domain/profile-card/profile-card-small/profile-card-small.component';
import { StringUtils } from '../../../shared/utils/string-utils';
import { BasicPageComponent } from '../../_shared/basic-page/basic-page.component';

@Component({
  selector: 'app-second-message',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,

    NzInputModule,
    NzIconModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzModalModule,
    NzAlertModule,
    NzPopconfirmModule,
    NzTabsModule,
    NzMessageModule,
    NzSelectModule,
    NzDropDownModule,
    NzTagModule,

    TruncatePipe,

    MatTooltip,

    EditableTextComponent,

    UpgradeToPremiumComponent,
    BasicPageComponent,
    NewEditMethodologyComponent,
    ProfileCardSmallComponent,
    PopoverEditTextComponent,
    CopyButtonComponent,
  ],
  templateUrl: './second-message.component.html',
  styleUrl: './second-message.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SecondMessageComponent {
  resumeList = toSignal(this.resumeService.resumeList$(), { initialValue: [] });
  productList = toSignal(this.productService.productList$(), { initialValue: [] });
  leadsList = toSignal(this.leadsService.myItemList$(), { initialValue: [] });

  linkedInIdResumeList = computed(() => {
    const list = this.resumeList();
    return list.filter((c) => !!c.subData.linkedInProviderId);
  });

  linkedInRespondedList = computed(() => {
    const list = this.leadsList();
    return list.filter((c) => c.status === LeadsStatus.linkedInResponded);
  });

  pendingApprovalsLeadsList = computed(() => {
    const list = this.leadsList();
    return list.filter((c) => c.status === LeadsStatus.linkedInSecondMessageDraft);
  });

  processingQueueLeadsList = computed(() => {
    const list = this.leadsList();
    return list.filter((c) => c.status === LeadsStatus.linkedInSecondMessageProcessing);
  });

  sentSecondMessageList = computed(() => {
    const list = this.leadsList();
    return list.filter((c) => c.status === LeadsStatus.linkedInSecondMessageSent);
  });

  failedSecondMessageList = computed(() => {
    const list = this.leadsList();
    return list.filter((c) => c.status === LeadsStatus.linkedInSecondMessageFailed);
  });

  linkedInResumeList = computed(() => {
    const list = this.resumeList();
    return list.filter((c) => c.subData.source === ResumeSource.linkedIn);
  });

  getResume = (id?: string) => computed(() => this.resumeList().find((resume) => resume.id === id));
  getProduct = (id?: string) => computed(() => this.productList().find((product) => product.id === id));

  secondMessageLeadsList = computed(() => {
    const list = this.leadsList();
    return list.filter((c) => !!c.unsafeSubData.linkedInInvitationId);
  });

  constructor(
    private productService: ProductService,
    private openaiService: OpenaiService,
    private resumeService: ResumeService,
    private unipileService: UnipileService,
    private messageService: NzMessageService,
    private leadsService: LeadsService,
    private boardHolderService: BoardHolderService
  ) {}

  ngOnInit() {}

  getLastWordFromCamelCase(input: string) {
    return StringUtils.getLastWordFromCamelCase(input);
  }

  onRegenerateMessageClick(data: Leads) {
    const resume = this.getResume(data?.unsafeSubData?.resumeId)();
    const product = this.getProduct(data?.unsafeSubData?.productId)();

    if (!resume || !product) {
      return;
    }

    return this.leadsService
      .generateSecondMessage$(resume, product)
      .pipe(take(1))
      .subscribe((answer) => {
        data.unsafeSubData.secondMessage = answer;
        this.leadsService.update$(data).pipe(take(1)).subscribe();
      });
  }

  onSendMessageClick(data: Leads) {
    this.unipileService.sendMessage$(data.id).pipe(take(1)).subscribe();
  }

  onSaveSecondMessageClick(data: Leads, text: string | undefined) {
    data.unsafeSubData.secondMessage = text;
    this.leadsService.update$(data).pipe(take(1)).subscribe();
  }

  onDeleteLeadsClick(data: Leads) {
    this.leadsService.deleteItemList$([data]).pipe(take(1)).subscribe();
  }

  onMarkAsDoneClick(data: Leads) {
    this.leadsService.changeLeadsStatus$(data, LeadsStatus.linkedInSecondMessageSent).pipe(take(1)).subscribe();
  }

  onApproveLeadsClick(data: Leads) {
    this.leadsService
      .changeLeadsStatus$(data, LeadsStatus.linkedInSecondMessageProcessing)
      .pipe(take(1))
      .subscribe((data) => {});

    // this.leadsService
    //   .addItemList$([{ unsafeSubData: { resumeId: item.resume.id, productId: item.product.id } }])
    //   .pipe(take(1))
    //   .subscribe();
  }
}
